import { useEffect, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';

const SITE_URL = 'https://bianhi.com';
const OG_IMAGE = 'https://i0.wp.com/bianhi.com/wp-content/uploads/2025/02/logo.png?fit=1698%2C721&ssl=1';

const setMetaTag = (attr, key, value) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const setLinkTag = (rel, hreflang, href) => {
  let selector = `link[rel="${rel}"]`;
  if (hreflang) selector += `[hreflang="${hreflang}"]`;
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hreflang) el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const buildStructuredData = (isEN) => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["MedicalBusiness", "LocalBusiness"],
      "@id": `${SITE_URL}/#organization`,
      "name": "Bianhi Medical Hair Restoration",
      "url": SITE_URL,
      "logo": OG_IMAGE,
      "image": OG_IMAGE,
      "description": isEN
        ? "FUE hair transplant specialists in Tijuana, Mexico. Over 7 years of experience with lifetime guarantee."
        : "Especialistas en trasplante capilar FUE en Tijuana, México. Más de 7 años de experiencia con garantía de por vida.",
      "telephone": "+52-664-514-2275",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Av. Diego Rivera 2386, 5to Piso Suite 503",
        "addressLocality": "Tijuana",
        "addressRegion": "Baja California",
        "postalCode": "22010",
        "addressCountry": "MX"
      },
      "geo": { "@type": "GeoCoordinates", "latitude": 32.5231912, "longitude": -117.0130642 },
      "openingHoursSpecification": [
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"], "opens": "09:00", "closes": "19:00" },
        { "@type": "OpeningHoursSpecification", "dayOfWeek": "Saturday", "opens": "09:00", "closes": "14:00" }
      ],
      "priceRange": "$$",
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "100", "bestRating": "5" },
      "sameAs": ["https://www.facebook.com/bianhitj", "https://www.instagram.com/bianhi.trasplantecapilarfue/"],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": isEN ? "Hair Restoration Services" : "Servicios de Restauración Capilar",
        "itemListElement": [
          { "@type": "MedicalProcedure", "name": isEN ? "FUE Hair Transplant" : "Trasplante Capilar FUE" },
          { "@type": "MedicalProcedure", "name": isEN ? "Beard and Mustache Graft" : "Injerto de Barba y Bigote" },
          { "@type": "MedicalProcedure", "name": isEN ? "Hair Graft for Women" : "Injerto Capilar para Mujeres" },
          { "@type": "MedicalProcedure", "name": isEN ? "Eyebrow Graft" : "Injerto de Cejas" }
        ]
      }
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": isEN ? "What is the FUE technique?" : "¿Qué es la técnica FUE?",
          "acceptedAnswer": { "@type": "Answer", "text": isEN
            ? "FUE (Follicular Unit Extraction) is a minimally invasive hair transplant technique that extracts individual follicular units, leaving no visible scars."
            : "FUE (Extracción de Unidades Foliculares) es una técnica de trasplante capilar mínimamente invasiva que extrae unidades foliculares individuales, sin dejar cicatrices visibles." }
        },
        {
          "@type": "Question",
          "name": isEN ? "Does Bianhi offer a guarantee?" : "¿Bianhi ofrece garantía?",
          "acceptedAnswer": { "@type": "Answer", "text": isEN
            ? "Yes, Bianhi offers a lifetime guarantee on all hair transplant procedures, backed by over 7 years of experience."
            : "Sí, Bianhi ofrece garantía de por vida en todos los procedimientos de trasplante capilar, respaldada por más de 7 años de experiencia." }
        },
        {
          "@type": "Question",
          "name": isEN ? "Where is Bianhi located?" : "¿Dónde está ubicado Bianhi?",
          "acceptedAnswer": { "@type": "Answer", "text": isEN
            ? "Bianhi is located at Edificio Hospital CER, Av. Diego Rivera 2386, 5th Floor Suite 503, Zona Urbana Rio, Tijuana, B.C., Mexico."
            : "Bianhi está ubicado en Edificio Hospital CER, Av. Diego Rivera 2386, 5to Piso Suite 503, Zona Urbana Rio, Tijuana, B.C., México." }
        }
      ]
    }
  ]
});

const SEOHead = ({ page = 'landing' }) => {
  const { language } = useLanguage();
  const isEN = language === 'en';

  const meta = useMemo(() => {
    const seo = {
      landing: {
        title: isEN
          ? 'Hair Transplant in Tijuana | Bianhi Medical Hair Restoration'
          : 'Trasplante Capilar en Tijuana | Bianhi Medical Hair Restoration',
        description: isEN
          ? 'FUE hair transplant specialists in Tijuana, Mexico. Hair transplants, beard grafts, eyebrow grafts. Lifetime guarantee. 7+ years of experience. Free consultation.'
          : 'Especialistas en trasplante capilar FUE en Tijuana, México. Trasplantes capilares, injerto de barba, injerto de cejas. Garantía de por vida. 7+ años de experiencia. Consulta gratuita.',
        keywords: isEN
          ? 'hair transplant tijuana, fue hair transplant mexico, beard graft, eyebrow graft, hair restoration tijuana, bianhi medical, hair transplant cost mexico'
          : 'trasplante capilar tijuana, injerto capilar fue mexico, injerto de barba, injerto de cejas, restauracion capilar tijuana, bianhi medical, costo trasplante capilar mexico',
        canonical: isEN ? `${SITE_URL}/en` : SITE_URL,
      },
      thankYou: {
        title: isEN
          ? 'Thank You | Bianhi Medical Hair Restoration'
          : 'Gracias | Bianhi Medical Hair Restoration',
        description: isEN
          ? 'Thank you for contacting us. Our team will get back to you shortly.'
          : 'Gracias por contactarnos. Nuestro equipo se comunicará contigo pronto.',
        keywords: '',
        canonical: isEN ? `${SITE_URL}/en/gracias` : `${SITE_URL}/gracias`,
      }
    };
    return seo[page] || seo.landing;
  }, [isEN, page]);

  // Update document title and lang
  useEffect(() => {
    document.documentElement.lang = isEN ? 'en' : 'es';
    document.title = meta.title;
  }, [isEN, meta.title]);

  // Update meta tags
  useEffect(() => {
    setMetaTag('name', 'description', meta.description);
    if (meta.keywords) setMetaTag('name', 'keywords', meta.keywords);
    setMetaTag('name', 'robots', page === 'thankYou' ? 'noindex, nofollow' : 'index, follow');
  }, [meta.description, meta.keywords, page]);

  // Update Open Graph and Twitter tags
  useEffect(() => {
    setMetaTag('property', 'og:title', meta.title);
    setMetaTag('property', 'og:description', meta.description);
    setMetaTag('property', 'og:url', meta.canonical);
    setMetaTag('property', 'og:image', OG_IMAGE);
    setMetaTag('property', 'og:locale', isEN ? 'en_US' : 'es_MX');
    setMetaTag('property', 'og:locale:alternate', isEN ? 'es_MX' : 'en_US');
    setMetaTag('name', 'twitter:title', meta.title);
    setMetaTag('name', 'twitter:description', meta.description);
  }, [meta.title, meta.description, meta.canonical, isEN]);

  // Update hreflang links
  useEffect(() => {
    const esUrl = page === 'thankYou' ? `${SITE_URL}/gracias` : SITE_URL;
    const enUrl = page === 'thankYou' ? `${SITE_URL}/en/gracias` : `${SITE_URL}/en`;
    setLinkTag('canonical', null, meta.canonical);
    setLinkTag('alternate', 'es', esUrl);
    setLinkTag('alternate', 'en', enUrl);
    setLinkTag('alternate', 'x-default', esUrl);
  }, [meta.canonical, page]);

  // Update structured data (JSON-LD)
  useEffect(() => {
    if (page !== 'landing') return;
    const structuredData = buildStructuredData(isEN);
    let script = document.getElementById('bianhi-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.id = 'bianhi-jsonld';
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  }, [isEN, page]);

  return null;
};

export default SEOHead;
